// components/WorkspaceColumnPickOverlay.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, RotateCcw, X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { buildLiveScreenSize, buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import { STREET_DEVELOP_RECIPES, streetDevelopErrors, toggleStreetCut } from '#lib/modelist/streetDevelop';
import { getProjectPanOffset, getProjectZoomLevel } from '#lib/project-runtime-layer-ui-state';
import { OverlayActionButton } from './NativeLayerCutOverlay';
import CreditsBadge from './ui/CreditsBadge';
import { PICK_FLOATING_UI_STYLE, PICK_INTERACTION_STYLE, pickToolbarStyle, quoteRecipeCredits, usePickEscape, useProjectViewport } from './workspacePickOverlay';

/** How near a click must fall to a cut, in screen pixels, to remove it rather than add one. */
const CUT_HIT_PX = 6;

const translations = {
  en: {
    slices: (count: number) => `${count} ${count === 1 ? 'slice' : 'slices'}`,
    note: (slice: number) => `Slice ${slice}: optional words`,
    develop: (count: number) => `Develop ${count} ${count === 1 ? 'slice' : 'slices'}`,
    reset: 'Clear the cuts',
    cancel: 'Cancel',
  },
  fr: {
    slices: (count: number) => `${count} ${count === 1 ? 'tranche' : 'tranches'}`,
    note: (slice: number) => `Tranche ${slice} : quelques mots, facultatif`,
    develop: (count: number) => `Développer ${count} ${count === 1 ? 'tranche' : 'tranches'}`,
    reset: 'Effacer les coupes',
    cancel: 'Annuler',
  },
} as const;

/** A Develop 360 run's price: one quote of the recipe's generation per slice. */
const developCredits = (recipeId: string, slices: number): number | null => {
  const recipe = STREET_DEVELOP_RECIPES.find((r) => r.id === recipeId);
  return recipe ? quoteRecipeCredits(recipe.model, recipe.params, recipe.prompt, slices) : null;
};

/**
 * The open workspace column pick (useCanvas: openWorkspaceColumnPick). A vertical line follows the pointer across the
 * frame. Single: a click on the frame commits that column, elsewhere leaves. Cuts: a click adds a cut, or removes the
 * one under it; each slice has a field for its own words, and a toolbar beside the frame shows the price and validates. Escape leaves. The middle button still pans the canvas beneath.
 */
const WorkspaceColumnPickOverlay = React.memo(() => {
  const { kernel } = useKernel();
  const pick = useKernelStore((state) => state.workspaceColumnPick);
  const frame = useKernelStore((state) => (
    state.workspaceColumnPick ? state.inventory.StudioWorkspace.registry[state.workspaceColumnPick.workspaceId]?.frameRect ?? null : null
  ));
  const { zoom, panX, panY } = useProjectViewport(pick?.projectId ?? null);
  const language = useKernelStore((state) => state.language);
  const t = translations[language];
  const line = useRef<HTMLDivElement>(null);

  const slices = pick?.mode === 'cuts' ? pick.cuts.at.length + 1 : 0;
  const recipeId = pick?.mode === 'cuts' ? pick.recipeId : null;
  const credits = useMemo(() => (recipeId ? developCredits(recipeId, slices) : null), [recipeId, slices]);

  const columnAt = useCallback((clientX: number, clientY: number) => {
    const k = kernel.current;
    const current = k?.getWorkspaceColumnPick();
    const container = k?.canvasContainerRef.current;
    const rect = current ? k?.getWorkspace(current.workspaceId)?.frameRect : undefined;
    if (!k || !current || !container || !rect) return null;
    const box = container.getBoundingClientRect();
    const pan = getProjectPanOffset(k, current.projectId), scale = getProjectZoomLevel(k, current.projectId);
    const x = (clientX - box.left - pan.x) / scale, y = (clientY - box.top - pan.y) / scale;
    return {
      pick: current,
      inside: x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h,
      fraction: (x - rect.x) / rect.w,
      tolerance: CUT_HIT_PX / (scale * rect.w),
      left: clientX - box.left, top: pan.y + rect.y * scale, height: rect.h * scale,
    };
  }, [kernel]);

  usePickEscape('data-workspace-column-pick', 'cancelWorkspaceColumnPick');

  // A pick does not outlive its workspace.
  const isWorkspaceGone = pick !== null && frame === null;
  useEffect(() => {
    if (isWorkspaceGone) kernel.current?.cancelWorkspaceColumnPick();
  }, [kernel, isWorkspaceGone]);

  const cuts = pick?.mode === 'cuts' ? pick.cuts : null;
  // The same refusal developStreet makes, shown before the click rather than after it.
  const errors = useMemo(() => {
    const recipe = STREET_DEVELOP_RECIPES.find((r) => r.id === recipeId);
    return recipe && cuts && frame ? streetDevelopErrors(recipe, cuts, frame) : [];
  }, [recipeId, cuts, frame]);
  const geometry = useMemo(() => {
    if (!cuts || !frame) return null;
    const edges = [0, ...cuts.at, 1];
    return {
      lines: cuts.at.map((cut) => ({
        key: cut,
        style: { left: buildLiveScreenX(frame.x + cut * frame.w, panX, zoom), top: buildLiveScreenY(frame.y, panY, zoom), height: buildLiveScreenSize(frame.h, zoom) },
      })),
      notes: cuts.notes.map((note, i) => ({
        key: edges[i]!,
        note,
        style: {
          left: buildLiveScreenX(frame.x + ((edges[i]! + edges[i + 1]!) / 2) * frame.w, panX, zoom),
          top: buildLiveScreenY(frame.y + frame.h / 2, panY, zoom),
          width: `min(220px, max(48px, ${buildLiveScreenSize((edges[i + 1]! - edges[i]!) * frame.w, zoom)} - 16px))`,
        },
      })),
    };
  }, [cuts, frame, panX, panY, zoom]);

  const move = useCallback((event: React.MouseEvent) => {
    const at = columnAt(event.clientX, event.clientY), el = line.current;
    if (!el) return;
    el.style.display = at?.inside ? 'block' : 'none';
    if (at?.inside) Object.assign(el.style, { left: `${at.left - 1}px`, top: `${at.top}px`, height: `${at.height}px` });
  }, [columnAt]);

  const down = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const k = kernel.current, at = columnAt(event.clientX, event.clientY);
    if (!k || !at) return;
    if (at.pick.mode === 'cuts') {
      if (at.inside) k.updateWorkspaceColumnPick(toggleStreetCut(at.pick.cuts, at.fraction, at.tolerance));
    } else if (at.inside) {
      void k.commitWorkspaceColumnPick(at.fraction);
    } else {
      k.cancelWorkspaceColumnPick();
    }
  }, [kernel, columnAt]);

  const swallow = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), []);
  const swallowLeft = useCallback((event: React.MouseEvent) => { if (event.button === 0) event.stopPropagation(); }, []);

  const changeNote = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const k = kernel.current, current = k?.getWorkspaceColumnPick();
    if (!k || current?.mode !== 'cuts') return;
    const slice = Number(event.currentTarget.dataset.slice), value = event.currentTarget.value;
    k.updateWorkspaceColumnPick({ ...current.cuts, notes: current.cuts.notes.map((note, i) => (i === slice ? value : note)) });
  }, [kernel]);

  const reset = useCallback(() => {
    const k = kernel.current, current = k?.getWorkspaceColumnPick();
    if (k && current?.mode === 'cuts') k.updateWorkspaceColumnPick({ ...current.cuts, at: [], notes: [''] });
  }, [kernel]);
  const validate = useCallback(() => { void kernel.current?.commitWorkspaceColumnPick(); }, [kernel]);
  const cancel = useCallback(() => kernel.current?.cancelWorkspaceColumnPick(), [kernel]);

  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  if (!pick || !frame || !portalHost) return null;

  const interaction = (
    <div style={PICK_INTERACTION_STYLE} onMouseDown={down} onMouseMove={move} onMouseUp={swallowLeft}>
      <div ref={line} className="pointer-events-none absolute hidden w-0.5 bg-[#3B82F6]" />
    </div>
  );
  if (pick.mode === 'single') return createPortal(interaction, portalHost);

  const toolbarStyle = pickToolbarStyle(frame, panX, panY, zoom, portalHost);

  const floating = (
    <div style={PICK_FLOATING_UI_STYLE} data-workspace-column-pick>
      {geometry?.lines.map(({ key, style }) => (
        <div key={key} className="absolute w-0.5 -translate-x-1/2 bg-[#3B82F6]" style={style} />
      ))}
      {geometry?.notes.map(({ key, note, style }, i) => (
        <input
          key={key}
          data-slice={i}
          value={note}
          onChange={changeNote}
          onMouseDown={swallow}
          onPointerDown={swallow}
          placeholder={t.note(i + 1)}
          aria-label={t.note(i + 1)}
          className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 rounded-md border border-white/10 bg-[#2A2A2A] px-2 py-1 text-xs text-white outline-none placeholder:text-white/40 focus:border-white/30"
          style={style}
        />
      ))}
      <div className="pointer-events-auto" style={toolbarStyle} onMouseDown={swallow} onPointerDown={swallow}>
        <div className="flex flex-col gap-1 rounded-lg bg-[#2A2A2A] p-1 text-xs text-white shadow-lg">
          <div className="flex items-center justify-between gap-2 px-1 py-1">
            <span className="text-white/50">{t.slices(slices)}</span>
            {credits !== null && <CreditsBadge value={String(credits)} size="sm" />}
          </div>
          {errors.length > 0 && <p className="px-1 text-white/50">{errors.join(' ')}</p>}
          <div className="flex items-center justify-end gap-1">
            <OverlayActionButton onClick={validate} title={errors[0] ?? t.develop(slices)} icon={Check} isActive={!errors.length} disabled={errors.length > 0} />
            {pick.cuts.at.length > 0 && <OverlayActionButton onClick={reset} title={t.reset} icon={RotateCcw} />}
            <OverlayActionButton onClick={cancel} title={t.cancel} icon={X} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(interaction, portalHost)}
      {createPortal(floating, portalHost)}
    </>
  );
});

WorkspaceColumnPickOverlay.displayName = 'WorkspaceColumnPickOverlay';
export default WorkspaceColumnPickOverlay;
